import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface DepartmentPerformanceScatterChartProps {
  data: Array<{
    department: string;
    workshop_count?: number;
    workshops_count?: number;
    average_progress?: number;
    avg_progress?: number;
    completion_rate: number;
  }>;
  loading?: boolean;
}

const DepartmentPerformanceScatterChart: React.FC<DepartmentPerformanceScatterChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Performance Scatter</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Performance Scatter</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No department data available
        </div>
      </div>
    );
  }

  // Generate colors for departments
  const colors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
  ];

  // Normalize data structure
  const normalizedData = data.map(item => ({
    department: item.department,
    workshop_count: item.workshop_count || item.workshops_count || 0,
    average_progress: item.average_progress || item.avg_progress || 0,
    completion_rate: item.completion_rate
  }));

  const getPerformanceLevel = (progress: number, completion: number) => {
    const score = (progress + completion) / 2;
    if (score >= 80) return { level: 'Excellent', color: '#10b981' };
    if (score >= 60) return { level: 'Good', color: '#3b82f6' };
    if (score >= 40) return { level: 'Average', color: '#f59e0b' };
    return { level: 'Needs Improvement', color: '#ef4444' };
  };

  const maxWorkshops = Math.max(...normalizedData.map(d => d.workshop_count));
  const maxProgress = Math.max(...normalizedData.map(d => d.average_progress));

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Performance Scatter</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart data={normalizedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number" 
              dataKey="workshop_count" 
              name="Workshop Count"
              tick={{ fontSize: 12 }}
              domain={[0, maxWorkshops + 2]}
            />
            <YAxis 
              type="number" 
              dataKey="average_progress" 
              name="Average Progress (%)"
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
            />
            <Tooltip 
              cursor={{ strokeDasharray: '3 3' }}
              formatter={(value: number, name: string, props: any) => {
                if (name === 'workshop_count') return [`${value} workshops`, 'Workshop Count'];
                if (name === 'average_progress') return [`${value.toFixed(1)}%`, 'Average Progress'];
                if (name === 'completion_rate') return [`${value.toFixed(1)}%`, 'Completion Rate'];
                return [value, name];
              }}
              labelFormatter={(label, payload) => {
                if (payload && payload[0]) {
                  const data = payload[0].payload;
                  return `Department: ${data.department}`;
                }
                return '';
              }}
            />
            <Scatter dataKey="average_progress" fill="#3b82f6">
              {normalizedData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={colors[index % colors.length]}
                  r={Math.max(6, Math.min(20, (entry.workshop_count / maxWorkshops) * 15 + 6))}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Department Analysis</h4>
          <div className="space-y-2">
            {[...normalizedData]
              .sort((a, b) => (b.average_progress + b.completion_rate) - (a.average_progress + a.completion_rate))
              .map((item, index) => {
                const performance = getPerformanceLevel(item.average_progress, item.completion_rate);
                return (
                  <div key={item.department} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded mr-2" 
                        style={{ backgroundColor: colors[index % colors.length] }}
                      ></div>
                      <span className="text-sm font-medium">{item.department}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{item.workshop_count}</span> workshops
                      <span className="ml-2 text-xs px-2 py-1 rounded" style={{ 
                        backgroundColor: performance.color + '20', 
                        color: performance.color 
                      }}>
                        {performance.level}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Performance Insights</h4>
          <div className="space-y-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Highest Volume</span>
                <span className="font-bold text-blue-600">
                  {normalizedData.reduce((max, item) => item.workshop_count > max.workshop_count ? item : max, normalizedData[0])?.department}
                </span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                {Math.max(...normalizedData.map(d => d.workshop_count))} workshops
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Best Performance</span>
                <span className="font-bold text-green-600">
                  {normalizedData.reduce((best, item) => 
                    (item.average_progress + item.completion_rate) > (best.average_progress + best.completion_rate) ? item : best, 
                    normalizedData[0]
                  )?.department}
                </span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                {Math.max(...normalizedData.map(d => d.average_progress)).toFixed(1)}% avg progress
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Workshops</span>
                <span className="font-bold text-gray-900">
                  {normalizedData.reduce((sum, item) => sum + item.workshop_count, 0)}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Across {normalizedData.length} departments
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentPerformanceScatterChart;
